<!--
  SupportTicketLinks.svelte — the tickets a ticket is linked to: the one it
  follows up on, and those that continue it.

  A closed ticket takes no replies, so a returning problem becomes a follow-up
  ticket; these links are what keep the story readable in both directions. Each
  names the ticket by key and subject and badges its status, so a reader can
  tell whether the continuation is still open without opening it. Links point
  into the reader's own side of the desk (`basePath`), which is also why the
  status badge takes the reader's `audience`.

  Renders nothing when the ticket is linked to nothing.
-->
<script lang="ts">
	import RoutedLink from './RoutedLink.svelte';
	import SupportStatusBadge from './SupportStatusBadge.svelte';
	import type { SupportLinkedTicket } from '../AppTypes';
	import type { SupportAudience } from '../support/supportFormat';
	import { t } from '../i18n';

	interface Props {
		/** The ticket this one follows up on, if any. */
		followUpOf: SupportLinkedTicket | null;
		/** Tickets that continue this one, oldest first. */
		followUps?: SupportLinkedTicket[];
		/** Route prefix of a ticket on the reader's side, e.g. `/Support/Tickets`. */
		basePath: string;
		/** Which side of the desk is reading — names the statuses. */
		audience?: SupportAudience;
	}

	let { followUpOf, followUps = [], basePath, audience = 'customer' }: Props = $props();
</script>

{#snippet linked(label: string, icon: string, linkedTicket: SupportLinkedTicket)}
	<li class="space-y-1">
		<p class="text-default text-xs">
			<i class="fa-solid {icon} me-1"></i>{label}
		</p>
		<div class="flex flex-wrap items-center gap-x-2 gap-y-1">
			<RoutedLink to="{basePath}/{encodeURIComponent(linkedTicket.ticketKey)}" class="min-w-0">
				<span class="font-mono">{linkedTicket.ticketKey}</span>
				<span>— {linkedTicket.subject}</span>
			</RoutedLink>
			<SupportStatusBadge status={linkedTicket.status} {audience} />
		</div>
	</li>
{/snippet}

{#if followUpOf || followUps.length > 0}
	<ul class="space-y-2 text-sm" aria-label={$t('Linked tickets')}>
		{#if followUpOf}
			{@render linked($t('Follow-up of'), 'fa-link', followUpOf)}
		{/if}
		{#each followUps as followUp (followUp.ticketKey)}
			{@render linked($t('Continued in'), 'fa-arrow-turn-down', followUp)}
		{/each}
	</ul>
{/if}
